import { Position } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import { ItemSelector } from 'bones/components/molecules/ItemSelector/ItemSelector';
import { useModelPropertyItems } from 'bones/components/molecules/QueryBuilder/hooks/selectorItems';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { getIntentClassName } from 'bones/utils/getIntentClassName';
import { getLocalName } from 'bones/utils/getLocalName';
import { Direction, OperatorType } from 'bones/utils/queryBuilder/constants';
import {
  PropertySelectItem,
  PropertySubSelectItem,
  createPropertySubSelectItem,
  getPropertyValue,
} from 'bones/utils/queryBuilder/helpers/select';
import { DraftFilter } from 'bones/utils/queryBuilder/types';

export interface PropertySelectorProps {
  invalid?: boolean;
  items: PropertySelectItem[];
  operator: DraftFilter['operator'];
  testId: string;
  tooltip: string;
  onSelect: (value: PropertySubSelectItem) => void;
}

export const PropertySelector: React.VFC<PropertySelectorProps> = ({
  invalid,
  items,
  operator,
  testId,
  tooltip,
  onSelect,
}) => {
  const { copy, testIds } = useQueryBuilderSchemaContext();
  const allPropertyItems = useModelPropertyItems().allProperties;

  const selectedValue = operator
    ? getPropertyValue(
        operator.iri,
        operator.operatorType !== OperatorType.OBJECT
          ? Direction.OUTBOUND
          : operator.direction
      )
    : null;

  let selectedItem: PropertySubSelectItem | null = null;
  const getSelectedItem = (maybeItem: PropertySelectItem) => {
    if (selectedItem) {
      return;
    }
    if (maybeItem.items) {
      maybeItem.items.forEach(getSelectedItem);
    } else if (maybeItem.value === selectedValue) {
      selectedItem = maybeItem as PropertySubSelectItem;
    }
  };
  items.forEach(getSelectedItem);

  if (!selectedItem) {
    selectedItem =
      allPropertyItems.find((item) => item.value === selectedValue) || null;
  }

  if (!selectedItem && operator?.iri) {
    selectedItem = createPropertySubSelectItem(
      {
        iri: operator.iri,
        label: getLocalName(operator.iri),
        operatorType: operator.operatorType,
        ranges: new Set(),
      },
      operator.direction || Direction.OUTBOUND
    );
  }

  const handleSelect = (item: PropertySelectItem | null) => {
    let foundItem: PropertySubSelectItem | null = null;
    const getFoundItem = (maybeItem: PropertySelectItem) => {
      if (foundItem) {
        return;
      }
      if (maybeItem.items) {
        maybeItem.items.forEach(getFoundItem);
      } else if (maybeItem.value === item?.value) {
        foundItem = maybeItem as PropertySubSelectItem;
      }
    };
    items.forEach(getFoundItem);

    // istanbul ignore else
    if (foundItem) {
      onSelect(foundItem);
    }
  };

  return (
    <ItemSelector.Single<PropertySubSelectItem, PropertySelectItem>
      buttonClassName={classNames({
        [getIntentClassName('sd-qb-black')]:
          selectedItem && selectedItem.operatorType === OperatorType.DATATYPE,
        [getIntentClassName('sd-qb-none')]:
          selectedItem && selectedItem.operatorType !== OperatorType.DATATYPE,
      })}
      buttonMinimal={Boolean(
        selectedItem && selectedItem.operatorType !== OperatorType.DATATYPE
      )}
      buttonOutlined={Boolean(
        selectedItem && selectedItem.operatorType === OperatorType.DATATYPE
      )}
      buttonRightIcon={selectedItem ? null : undefined}
      buttonText={
        selectedItem ? selectedItem.label : copy.body.property.placeholder
      }
      className={classNames('sd-query-builder-property-selector', {
        'sd-query-builder-property-selector--invalid': invalid,
      })}
      filterable
      items={items}
      itemTestId={testIds.propertySelectorItem}
      onSelect={handleSelect}
      popoverProps={{
        hasBackdrop: true,
      }}
      selected={selectedItem}
      subItemTestId={testIds.propertySelectorSubItem}
      testId={testId}
      tooltipProps={{
        content: tooltip,
        disabled: !(tooltip && selectedItem && invalid),
        popoverClassName: 'sd-query-builder-property-selector-tooltip',
        position: Position.RIGHT,
      }}
    />
  );
};
